// Mirrors src/liando_agent/api/schemas.py — keep field names in lockstep.

export interface ErrorDetail {
  code: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

// ---- auth ---------------------------------------------------------------

export interface LoginResponse {
  token: string;
  user_id: string;
  username: string;
  role: "admin" | "user";
  expires_in_seconds: number;
}

export interface WhoAmIResponse {
  user_id: string;
  username: string;
  role: "admin" | "user";
  disabled: boolean;
}

// ---- sessions -----------------------------------------------------------

export interface TaskAcceptedResponse {
  task_id: string;
  user_id: string;
  state: string;
  status_url: string;
}

export type TaskState = "queued" | "running" | "done" | "error" | "cancelled";

export interface TaskStatusResponse {
  task_id: string;
  user_id: string;
  state: TaskState;
  session_id: string | null;
  pdf_url: string | null;
  error_code: string | null;
  error_msg: string | null;
  created_at: number | null;
  started_at: number | null;
  finished_at: number | null;
  // request snapshot (populated when the task row was created with it)
  title: string | null;
  section_heading: string | null;
  rule: string | null;
}

export interface TaskListResponse {
  tasks: TaskStatusResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface TraceStateRow {
  ts: number;
  from_state: string | null;
  to_state: string;
  outcome: string | null;
  label: string;
  step_index: number;
}

export interface TraceToolRow {
  ts: number;
  step_index: number;
  tool: string;
  label: string;
  args_hash: string;
  duration_ms: number | null;
  outcome: string;
  error_code: string | null;
  result_preview: string | null;
}

export interface TraceLLMRow {
  ts: number;
  step_index: number;
  model: string;
  prompt_tokens: number | null;
  output_tokens: number | null;
  cached_tokens: number | null;
  finish_reason: string | null;
  thought_preview: string | null;
  tool_proposed: string | null;
}

export interface StepLabel {
  key: string;
  label: string;
}

export interface SessionTraceResponse {
  session_id: string;
  user_id: string;
  current_state: string;
  terminal: boolean;
  states: TraceStateRow[];
  tool_calls: TraceToolRow[];
  llm_calls: TraceLLMRow[];
  all_step_labels: StepLabel[];
}

// ---- transport ----------------------------------------------------------

type Json = Record<string, unknown> | unknown[];

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  json?: Json;
  form?: FormData;
  token?: string | null;
  signal?: AbortSignal;
}

async function parseError(res: Response): Promise<ApiError> {
  let code = "http_error";
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as { detail?: ErrorDetail | string };
    if (body.detail) {
      if (typeof body.detail === "string") {
        message = body.detail;
      } else {
        code = body.detail.code;
        message = body.detail.message;
      }
    }
  } catch {
    // body wasn't JSON — keep defaults
  }
  return new ApiError(res.status, code, message);
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;
  if (opts.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.json);
  } else if (opts.form !== undefined) {
    body = opts.form; // browser sets multipart boundary
  }
  if (opts.token) {
    headers.Authorization = `Bearer ${opts.token}`;
  }
  const res = await fetch(path, {
    method: opts.method ?? (body ? "POST" : "GET"),
    headers,
    body,
    signal: opts.signal,
  });
  if (!res.ok) {
    throw await parseError(res);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

// ---- auth helpers -------------------------------------------------------

export interface UserOut {
  user_id: string;
  username: string;
  role: "admin" | "user";
  disabled: boolean;
  created_at: number;
}

export interface UserListResponse {
  users: UserOut[];
}

export interface OAStatusResponse {
  password_login_enabled: boolean;
  allow_auto_provision: boolean;
  sso_configured: boolean;
}

export interface OALoginUrlResponse {
  login_url: string;
  service: string;
}

export const authApi = {
  login: (username: string, password: string) =>
    request<LoginResponse>("/auth/login", { json: { username, password } }),
  initAdmin: (username: string, password: string) =>
    request<{ user_id: string }>("/auth/init-admin", {
      json: { username, password },
    }),
  whoami: (token: string) => request<WhoAmIResponse>("/auth/whoami", { token }),
  logout: (token: string) => request<void>("/auth/logout", { method: "POST", token }),
  oaStatus: () => request<OAStatusResponse>("/auth/oa/status"),
  oaLoginUrl: (service: string) =>
    request<OALoginUrlResponse>(`/auth/oa/login-url?service=${encodeURIComponent(service)}`),
  oaCallback: (ticket: string, service: string) =>
    request<LoginResponse>("/auth/oa/callback", {
      method: "POST",
      json: { ticket, service },
    }),
  listUsers: (token: string, includeDisabled = false) =>
    request<UserListResponse>(
      `/auth/users?include_disabled=${includeDisabled ? "true" : "false"}`,
      { token },
    ),
  createUser: (
    token: string,
    body: { username: string; password: string; role: "admin" | "user" },
  ) => request<UserOut>("/auth/users", { method: "POST", json: body, token }),
  patchUser: (token: string, userId: string, body: { disabled: boolean }) =>
    request<UserOut>(`/auth/users/${userId}`, { method: "PATCH", json: body, token }),
};

// ---- sessions helpers ---------------------------------------------------

export interface SubmitFormFields {
  file: File;
  rule: string;
  title: string;
  section_heading: string;
  sheet?: string;
  header_row?: number;
}

export interface ListTasksParams {
  state?: TaskState[];
  limit?: number;
  offset?: number;
}

export const sessionsApi = {
  submit: (token: string, fields: SubmitFormFields) => {
    const fd = new FormData();
    fd.append("file", fields.file);
    fd.append("rule", fields.rule);
    fd.append("title", fields.title);
    fd.append("section_heading", fields.section_heading);
    if (fields.sheet) fd.append("sheet", fields.sheet);
    fd.append("header_row", String(fields.header_row ?? 1));
    return request<TaskAcceptedResponse>("/sessions", { form: fd, token });
  },
  getTask: (token: string, taskId: string) =>
    request<TaskStatusResponse>(`/sessions/tasks/${taskId}`, { token }),
  listTasks: (token: string, params: ListTasksParams = {}) => {
    const q = new URLSearchParams();
    if (params.state?.length) q.set("state", params.state.join(","));
    if (params.limit !== undefined) q.set("limit", String(params.limit));
    if (params.offset !== undefined) q.set("offset", String(params.offset));
    const qs = q.toString();
    return request<TaskListResponse>(`/sessions/tasks${qs ? `?${qs}` : ""}`, { token });
  },
  getTaskTrace: (token: string, taskId: string) =>
    request<SessionTraceResponse>(`/sessions/tasks/${taskId}/trace`, { token }),
  getSessionTrace: (token: string, sessionId: string) =>
    request<SessionTraceResponse>(`/sessions/${sessionId}/trace`, { token }),
  pdfUrl: (sessionId: string): string => `/sessions/${sessionId}/pdf`,
};

// ---- land research (产研部拿地研判表单页) --------------------------------

export interface LandResearchSubmitFields {
  lat: string;
  lng: string;
  projectName: string;
  analyst?: string;
  landConditions?: File | null;
  competitor?: File | null;
  city?: File | null;
  qccCompanyTotal?: number | null;
  qccSelectionJson?: string;
  conclusionModel?: string;
}

export interface LandResearchSubmitResponse {
  conv_id: string;
  project_name: string;
  uploaded: string[];
}

export interface LandResearchModelOptionsResponse {
  enabled: boolean;
  default: string;
  options: Array<{ value: string; label: string }>;
}

export interface QccMapSessionResponse {
  session_id: string;
  expires_in_seconds: number;
  entry_url: string;
  account_connected: boolean;
}

export interface QccIndustryBucket {
  code: string;
  name: string;
  count: number;
}
export interface QccMapSelectionResponse {
  total: number | null;
  updated_at: number | null;
  industry?: QccIndustryBucket[];
  active_total?: number | null;
}

export interface QccMapAccountResponse {
  connected: boolean;
  updated_at: number | null;
}

export interface LandReportContentRow {
  row: number;
  values: string[];
}

export interface LandReportImage {
  name: string;
  mime: string;
  data_url: string;
  width?: number | null;
  height?: number | null;
  row?: number | null;
  column?: number | null;
}

export interface LandReportChart {
  name: string;
  type: string;
}

export interface LandReportSheet {
  name: string;
  title: string;
  row_count: number;
  column_count: number;
  content_rows: LandReportContentRow[];
  images: LandReportImage[];
  charts: LandReportChart[];
}

export interface LandReportModuleItem {
  label: string;
  description: string;
  source: string;
  value: string;
}

export interface LandReportModule {
  key: string;
  title: string;
  summary: string;
  items: LandReportModuleItem[];
}

export interface LandReportTable {
  title: string;
  sheet: string;
  columns: string[];
  rows: string[][];
}

export interface LandReportSource {
  module: string;
  label: string;
  source: string;
  value: string;
}

export interface LandPreviewCard {
  label: string;
  value: string;
  tone?: "primary" | "success" | "neutral";
}

export interface LandPreviewItem {
  label: string;
  value: string;
}

export interface LandPreviewSimpleTable {
  columns: string[];
  rows: string[][];
}

export interface LandPreviewBlockTool {
  summary_cards: LandPreviewCard[];
  decision_opinion: string;
  location_items: LandPreviewItem[];
  demand_items: LandPreviewItem[];
  evidence_images?: LandPreviewEvidenceImage[];
  supply_items: LandPreviewItem[];
  supply_trend: LandPreviewSimpleTable;
  scarcity_items: LandPreviewItem[];
  land_condition_table: LandPreviewSimpleTable;
  competitor_table: LandPreviewSimpleTable;
  strategy_items: LandPreviewItem[];
}

export interface LandPreviewLeadBlock {
  kind: "title" | "section" | "text";
  text: string;
}

export interface LandPreviewRecordField {
  label: string;
  value: string;
  parts?: Array<{ kind: "text" | "link"; text: string; href?: string }>;
}

export interface LandPreviewEvidenceImage {
  label: string;
  url: string;
}

export interface LandPreviewRecord {
  title: string;
  section?: string;
  category?: string;
  fields: LandPreviewRecordField[];
  evidence_images?: LandPreviewEvidenceImage[];
}

export interface LandPreviewDetailSheet {
  name: string;
  title: string;
  row_count: number;
  column_count: number;
  lead_blocks: LandPreviewLeadBlock[];
  records: LandPreviewRecord[];
  images: LandReportImage[];
  charts: LandReportChart[];
}

export interface LandPreviewQualityFact {
  fact_id: string;
  metric: string;
  value: string;
  normalized_value: string;
  unit: string;
  region: string;
  caliber: string;
  stat_year: string;
  origin: string;
  source_grade: string;
  status: string;
  confidence: string;
  evidence_count: string;
  input_fact_ids: string;
  evidence_ids: string;
}

export interface LandPreviewQualityEvidence {
  evidence_id: string;
  fact_id: string;
  source_type: string;
  source_title: string;
  source_org: string;
  source_url: string;
  fetched_at: string;
  http_status: string;
  content_type: string;
  publication_date: string;
  stat_year: string;
  quote: string;
  page: string;
  table_name: string;
  cell_range: string;
  bounding_box: string;
  query_params: string;
  screenshot_path: string;
  content_sha256: string;
  validation_status: string;
  validation_reasons: string;
}

export interface LandPreviewQualityRepair {
  repair_id: string;
  fact_id: string;
  trigger_reason: string;
  before_value: string;
  before_year: string;
  repair_strategy: string;
  attempt: string;
  after_value: string;
  after_year: string;
  result: string;
  affected_cells: string;
  affected_conclusions: string;
}

export interface LandPreviewQualityConclusion {
  conclusion_id: string;
  sheet: string;
  cell: string;
  verdict: string;
  confidence_level: string;
  fact_id: string;
  fact_role: string;
  fact_status: string;
  scenario: string;
  threshold: string;
}

export interface LandPreviewQuality {
  summary: {
    fact_count: number;
    evidence_count: number;
    repair_count: number;
    conclusion_count: number;
    fact_evidence_coverage: number;
    statuses: Record<string, number>;
  };
  facts: LandPreviewQualityFact[];
  evidence: LandPreviewQualityEvidence[];
  repairs: LandPreviewQualityRepair[];
  conclusions: LandPreviewQualityConclusion[];
}

export interface LandIndustryStrippingItem {
  industry: string;
  enterprise_count: number | null;
  revenue_yi: number | null;
  default_classification: string;
  classification: string;
  retention_ratio: number;
  excluded_enterprises: number | null;
  effective_enterprises: number | null;
  note: string;
  editable: boolean;
}

export interface LandIndustryStripping {
  sheet_name: string;
  title: string;
  caliber_note: string;
  total_enterprises: number | null;
  excluded_enterprises: number | null;
  effective_enterprises: number | null;
  adjustment_status: string;
  updated_at: string;
  total_revenue_yi: number | null;
  excluded_revenue_yi: number | null;
  effective_revenue_yi: number | null;
  source: string;
  industries: LandIndustryStrippingItem[];
  editable: boolean;
}

export interface LandIndustryStrippingUpdateResponse {
  written: boolean;
  preferences_saved: boolean;
  preference_count: number;
  artifact_ref_id: string;
  report_sha256: string;
  size_bytes: number;
  industry_stripping: LandIndustryStripping;
}

export interface LandIndustryStrippingStateResponse {
  artifact_ref_id: string;
  report_sha256: string;
  size_bytes: number;
  industry_stripping: LandIndustryStripping;
}

export interface LandResearchProgressStage {
  key: string;
  label: string;
  status: "pending" | "running" | "done" | "error";
}

export interface LandResearchProgressActivity {
  ts: string;
  label: string;
  status: "running" | "done";
}

export interface LandResearchProgressResponse {
  status: "pending" | "running" | "done" | "error";
  stage_key: string;
  stage_label: string;
  stage_index: number;
  stage_total: number;
  detail: string;
  error_message?: string;
  started_at: string;
  updated_at: string;
  elapsed_seconds: number;
  event_count: number;
  stages: LandResearchProgressStage[];
  recent: LandResearchProgressActivity[];
}

export interface LandResearchPreviewResponse {
  schema_version?: number;
  name: string;
  artifact_ref_id?: string | null;
  report_sha256?: string;
  project_name: string;
  recommendation: string;
  scarcity: string;
  summary: string;
  block_tool?: LandPreviewBlockTool;
  detail_sheets?: LandPreviewDetailSheet[];
  sheets?: LandReportSheet[];
  modules: LandReportModule[];
  tables?: LandReportTable[];
  sources: LandReportSource[];
  quality?: LandPreviewQuality;
  image_assets?: Array<{ id: string; mime: string; size_bytes: number }>;
  industry_stripping?: LandIndustryStripping | null;
}

export interface LandResearchBusinessRule {
  business_goal: string;
  required_evidence: string[];
  evidence_priority: string[];
  decision_conditions: string[];
  insufficient_evidence: string;
  output_order: string[];
  forbidden_content: string[];
  reference_example: string;
  supporting_projects: string[];
  needs_confirmation: string[];
  confidence: number;
}

export interface LandResearchPublishedRuleTemplate {
  entry_id: string;
  rule: string;
  business_rule: LandResearchBusinessRule;
  model: string;
  source_projects: string[];
  published_by: string;
  published_at: number;
}

export interface LandResearchLearnedRuleDraft {
  ref: string;
  rule: string;
  business_rule: LandResearchBusinessRule;
  understanding_summary: string;
  evidence_checks: Array<{
    claim: string;
    status: "supported" | "needs_verification" | "conflict";
    basis: string;
  }>;
  analysis_summary: {
    evidence_coverage?: string;
    case_count?: number;
  };
  source_projects: string[];
  source_sample_count: number;
  model: string;
  review_status: "pending" | "published";
}

export interface LandResearchRuleReferenceSample {
  project_name: string;
  source_file: string;
  final_conclusion: string;
  business_question: string;
  expected_evidence: string;
  audit_passed: boolean;
}

export interface LandResearchCellRulePolishResponse {
  ref: string;
  field_label: string;
  polished_rule: string;
  business_rule: LandResearchBusinessRule;
  understanding_summary: string;
  evidence_checks: Array<{
    claim: string;
    status: "supported" | "needs_verification" | "conflict";
    basis: string;
  }>;
  system_adjustments: Array<{
    original_text: string;
    interpreted_text: string;
    reason: string;
  }>;
  template_guide: {
    title: string;
    source_note: string;
    source_projects: string[];
    structure: string[];
    worked_example: string;
    feedback_example: string;
  };
  reference_samples: LandResearchRuleReferenceSample[];
  sample_count: number;
  source_projects: string[];
  model: string;
  developer_review_only: true;
  batch_learned?: boolean;
}

export interface LandResearchCellRuleResponse {
  ref: string;
  field_label: string;
  editable: boolean;
  rule_status: "available" | "source_rule_missing";
  rule_origin: "cell_generation_rule" | "workbook_profile" | "";
  original_rule: string;
  effective_rule: string;
  original_business_rule: LandResearchBusinessRule;
  effective_business_rule: LandResearchBusinessRule;
  published_template?: LandResearchPublishedRuleTemplate | null;
  learned_draft?: LandResearchLearnedRuleDraft | null;
  rule_target_ref?: string;
  active_user_rule?: {
    entry_id: string;
    rule: string;
    raw_rule?: string;
    state: "active" | "probation" | "local";
    scope: "this_report" | "all_reports";
    applies_to_current_report: boolean;
  } | null;
  generation_requirement: string;
  evidence: string;
  current_conclusion: string;
  generation_rule?: {
    sheet: string;
    cell: string;
    business_question: string;
    expected_evidence: string;
    context: string;
    output_contract: string[];
    expert_rule: string;
    injected_rule_ids: string[];
    injected_rules: Array<{ rule_id: string; rule: string; 状态: string }>;
    snapshot_source: "generation_audit" | "template_task";
  };
  generation_rule_provenance: {
    status: "exact" | "reconstructed";
    label: string;
    detail: string;
  };
  derivation_audit?: {
    source?: string;
    reason?: string;
    confidence?: number;
    evidence_items?: string[];
    reasoning_summary?: string;
  } | null;
  source_rules: Array<{
    source_file: string;
    module: string;
    module_label?: string;
    instruction: string;
    required_evidence: string[];
    assigned_ranges: Record<string, string[]>;
    match_basis: "generation_audit" | "report_field";
  }>;
}

export interface LandResearchCellRuleApplyResponse {
  saved: true;
  refilled: boolean;
  field_label: string;
  old_value?: string;
  new_value?: string;
  receipt_line: string;
  rule_state: string;
  synced_cells: string[];
  report_sha256?: string;
  report_version?: string;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error ?? new Error("preview image decode failed"));
    reader.readAsDataURL(blob);
  });
}

async function hydratePreviewAssets(
  preview: LandResearchPreviewResponse,
  token: string,
  convId: string,
): Promise<LandResearchPreviewResponse> {
  const assets = preview.image_assets ?? [];
  if (!assets.length) return preview;
  const resolved = new Map<string, string>();
  await Promise.all(
    assets.map(async (asset) => {
      const response = await fetch(`/land-research/${convId}/preview/assets/${asset.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw await parseError(response);
      resolved.set(asset.id, await blobToDataUrl(await response.blob()));
    }),
  );

  const visit = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(visit);
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, visit(item)]),
      );
    }
    if (typeof value === "string" && value.startsWith("preview-asset:")) {
      return resolved.get(value.slice("preview-asset:".length)) ?? "";
    }
    return value;
  };
  return visit(preview) as LandResearchPreviewResponse;
}

export const landResearchApi = {
  modelOptions: (token: string) =>
    request<LandResearchModelOptionsResponse>("/land-research/model-options", { token }),
  submit: (token: string, fields: LandResearchSubmitFields) => {
    const fd = new FormData();
    fd.append("lat", fields.lat);
    fd.append("lng", fields.lng);
    fd.append("project_name", fields.projectName);
    if (fields.analyst) fd.append("analyst", fields.analyst);
    if (fields.landConditions) fd.append("parcel", fields.landConditions);
    if (fields.competitor) fd.append("competitor", fields.competitor);
    if (fields.city) fd.append("city", fields.city);
    if (fields.qccCompanyTotal != null) {
      fd.append("qcc_company_total", String(fields.qccCompanyTotal));
    }
    if (fields.qccSelectionJson) {
      fd.append("qcc_selection_json", fields.qccSelectionJson);
    }
    fd.append("conclusion_model", fields.conclusionModel ?? "default");
    return request<LandResearchSubmitResponse>("/land-research", { form: fd, token });
  },
  streamUrl: (convId: string): string => `/land-research/${convId}/stream`,
  progress: (token: string, convId: string) =>
    request<LandResearchProgressResponse>(`/land-research/${convId}/progress`, { token }),
  reportUrl: (convId: string): string => `/land-research/${convId}/report`,
  cellRule: (token: string, convId: string, cellRef: string) =>
    request<LandResearchCellRuleResponse>(
      `/land-research/${convId}/cell-rule?ref=${encodeURIComponent(cellRef)}`,
      { token },
    ),
  applyCellRule: (
    token: string,
    convId: string,
    body: {
      ref: string;
      rule: string;
      rawRule?: string;
      scope: "this_report" | "all_reports";
      applyToCurrentReport?: boolean;
    },
  ) =>
    request<LandResearchCellRuleApplyResponse>(`/land-research/${convId}/cell-rule`, {
      method: "PUT",
      token,
      json: {
        ref: body.ref,
        rule: body.rule,
        raw_rule: body.rawRule ?? body.rule,
        scope: body.scope,
        apply_to_current_report: body.applyToCurrentReport ?? true,
      },
    }),
  polishCellRule: (token: string, convId: string, ref: string) =>
    request<LandResearchCellRulePolishResponse>(`/land-research/${convId}/cell-rule/polish`, {
      method: "POST",
      token,
      json: { ref, feedback: "", models: ["default"] },
    }),
  publishCellRuleTemplate: (
    token: string,
    convId: string,
    body: {
      ref: string;
      businessRule: LandResearchBusinessRule;
      model: string;
      sourceProjects: string[];
    },
  ) =>
    request<{
      published: true;
      ref: string;
      field_label: string;
      template: LandResearchPublishedRuleTemplate;
    }>(`/land-research/${convId}/cell-rule/template`, {
      method: "PUT",
      token,
      json: {
        ref: body.ref,
        business_rule: body.businessRule,
        model: body.model,
        source_projects: body.sourceProjects,
      },
    }),
  preview: (token: string, convId: string) =>
    request<LandResearchPreviewResponse>(`/land-research/${convId}/preview`, { token }),
  previewCompact: async (token: string, convId: string) => {
    const preview = await request<LandResearchPreviewResponse>(
      `/land-research/${convId}/preview/compact`,
      { token },
    );
    return hydratePreviewAssets(preview, token, convId);
  },
  updateIndustryStripping: (
    token: string,
    convId: string,
    sourceSha256: string,
    items: Array<{ industry: string; retention_ratio: number }>,
  ) =>
    request<LandIndustryStrippingUpdateResponse>(`/land-research/${convId}/industry-stripping`, {
      method: "PUT",
      token,
      json: { source_sha256: sourceSha256, items },
    }),
  industryStripping: (token: string, convId: string) =>
    request<LandIndustryStrippingStateResponse>(`/land-research/${convId}/industry-stripping`, {
      token,
    }),
  // Tencent map key for the 地图选点 picker — served from the backend's
  // TENCENT_MAP_KEY so the front-end needs no separate build-time config.
  mapKey: (token: string) => request<{ key: string }>("/land-research/map-key", { token }),
  qccMapSession: (token: string) =>
    request<QccMapSessionResponse>("/qcc-map/sessions", { method: "POST", token }),
  qccMapSelection: (token: string, sessionId: string) =>
    request<QccMapSelectionResponse>(`/qcc-map/sessions/${sessionId}/selection`, { token }),
  qccMapAccount: (token: string) => request<QccMapAccountResponse>("/qcc-map/account", { token }),
  disconnectQccMapAccount: (token: string) =>
    request<QccMapAccountResponse>("/qcc-map/account", { method: "DELETE", token }),
};

export async function downloadLandResearchReport(
  token: string,
  convId: string,
  suggestedName: string,
): Promise<void> {
  const res = await fetch(landResearchApi.reportUrl(convId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw await parseError(res);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const responseName = filenameFromContentDisposition(res.headers.get("content-disposition"));
  a.href = url;
  a.download = responseName || suggestedName;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- memory -------------------------------------------------------------

export type MemoryKind =
  | "business.field_mapping"
  | "business.term"
  | "user.role"
  | "user.preference"
  | "user.rule_template"
  | "user.episodic_event"
  | "skill.rule_template";

export interface MemoryEntryOut {
  id: string;
  kind: MemoryKind;
  natural_text: string;
  payload: Record<string, unknown>;
  confidence: number;
  version: number;
  hit_count: number;
  quarantined: boolean;
  archived?: boolean;
  superseded_by?: string | null;
  related_ids?: string[];
  // rule_template 类专有: payload.dsl_skeleton 渲染出的中文自然语言 (确定性)
  rule_description?: string | null;
}

export interface MemoryEntryUpdateResponse {
  status: "updated" | "pending_review";
  entry: MemoryEntryOut | null;
  review_id: string | null;
}

export interface RuleDescribeResponse {
  description: string;
  schema_errors: string[];
}

export interface MemoryEntryListResponse {
  entries: MemoryEntryOut[];
}

export interface QuarantineResult {
  quarantined: string[];
  rejected: { entry_id: string; reason: string }[];
}

export interface AuditRow {
  ts: number;
  op: string;
  entry_id: string;
  kind: string;
  scope: string;
  acting_user_id: string | null;
  decided_by: string;
  confidence: number | null;
  note: string | null;
}

export interface AuditListResponse {
  rows: AuditRow[];
}

export const memoryApi = {
  listEntries: (
    token: string,
    params: {
      kinds?: MemoryKind[];
      includeQuarantined?: boolean;
      includeSuperseded?: boolean;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.kinds?.length) q.set("kinds", params.kinds.join(","));
    if (params.includeQuarantined) q.set("include_quarantined", "true");
    if (params.includeSuperseded) q.set("include_superseded", "true");
    const qs = q.toString();
    return request<MemoryEntryListResponse>(`/memory/entries${qs ? `?${qs}` : ""}`, { token });
  },
  getEntry: (token: string, entryId: string) =>
    request<MemoryEntryOut>(`/memory/entries/${entryId}`, { token }),
  updateEntry: (
    token: string,
    entryId: string,
    body: {
      natural_text?: string;
      payload?: Record<string, unknown>;
      confidence?: number;
    },
  ) =>
    request<MemoryEntryUpdateResponse>(`/memory/entries/${entryId}`, {
      method: "PUT",
      json: body,
      token,
    }),
  describeRule: (token: string, dsl: Record<string, unknown>) =>
    request<RuleDescribeResponse>("/memory/rules/describe", {
      method: "POST",
      json: { dsl },
      token,
    }),
  quarantine: (token: string, entryIds: string[], reason?: string) =>
    request<QuarantineResult>("/memory/quarantine", {
      method: "POST",
      json: { entry_ids: entryIds, reason: reason ?? null },
      token,
    }),
  consolidate: (token: string, archiveDays = 90) =>
    request<{ archived: string[] }>(`/memory/consolidate?archive_days=${archiveDays}`, {
      method: "POST",
      token,
    }),
  listAudit: (token: string, sinceDays = 7) =>
    request<AuditListResponse>(`/memory/audit?since_days=${sinceDays}`, { token }),
};

// ---- PDF download with Authorization (EventSource/anchor can't set headers) ---

export async function downloadPdf(
  token: string,
  sessionId: string,
  suggestedName: string,
): Promise<void> {
  const res = await fetch(sessionsApi.pdfUrl(sessionId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw await parseError(res);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- conversations (chat) -------------------------------------------------

export interface ConversationOut {
  conv_id: string;
  user_id: string;
  title: string | null;
  created_at: number;
  last_message_at: number;
  archived: boolean;
  conversation_type: "chat" | "land_research";
  land_status: "pending" | "running" | "done" | "error" | null;
}

export interface ConversationListResponse {
  conversations: ConversationOut[];
}

export interface MessageOut {
  msg_id: string;
  conv_id: string;
  role: "system" | "user" | "assistant" | "tool";
  content: string | null;
  tool_calls?: Array<{
    id?: string;
    type?: string;
    function?: { name?: string; arguments?: string };
  }> | null;
  tool_call_id?: string | null;
  tool_name?: string | null;
  // Streamed model "thinking" — null on tool / user messages, may be null
  // on older assistant rows (pre-schema-migration).
  reasoning_content?: string | null;
  ts: number;
  seq: number;
}

export interface MessageListResponse {
  messages: MessageOut[];
}

export interface AttachmentOut {
  artifact_ref_id: string;
  conv_id: string;
  msg_id: string | null;
  kind: "upload" | "pdf" | "xlsx" | "parsed_file" | "map_view";
  name: string;
  size_bytes: number | null;
  created_at: number;
}

export interface SpreadsheetSheet {
  name: string;
  columns: string[];
  row_count: number;
  rows: Record<string, unknown>[];
}

export interface MapCoord {
  lat: number;
  lng: number;
}

export interface MapBounds {
  min_lat: number;
  min_lng: number;
  max_lat: number;
  max_lng: number;
}

export interface MapLayer {
  id: string;
  label: string;
  visible?: boolean;
}

export interface MapPoint extends MapCoord {
  id: string;
  label: string;
  type?: string;
  layer?: string;
  color?: string;
  meta?: Record<string, unknown>;
}

export interface MapCircle extends MapCoord {
  id: string;
  label: string;
  radius_m: number;
  layer?: string;
  color?: string;
  meta?: Record<string, unknown>;
}

export interface MapLine {
  id: string;
  label: string;
  from: MapCoord;
  to: MapCoord;
  path?: MapCoord[];
  layer?: string;
  color?: string;
  meta?: Record<string, unknown>;
}

export interface MapViewSpec {
  version: number;
  title: string;
  center: MapCoord;
  bounds: MapBounds;
  layers: MapLayer[];
  points: MapPoint[];
  circles: MapCircle[];
  lines: MapLine[];
}

export type FilePreviewResponse =
  | {
      kind: "spreadsheet";
      name: string;
      size_bytes: number | null;
      sheets: SpreadsheetSheet[];
    }
  | {
      kind: "map";
      name: string;
      size_bytes: number | null;
      spec: MapViewSpec;
      download_url: string;
    }
  | {
      kind: "text";
      name: string;
      size_bytes: number | null;
      language: "text" | "markdown";
      content: string;
      truncated: boolean;
      download_url: string;
    }
  | {
      kind: "binary";
      name: string;
      size_bytes: number | null;
      download_url: string;
    };

export const conversationsApi = {
  list: (token: string, archived = false) =>
    request<ConversationListResponse>(`/conversations?archived=${archived ? "true" : "false"}`, {
      token,
    }),
  create: (token: string, title?: string) =>
    request<ConversationOut>("/conversations", {
      json: { title: title ?? null },
      token,
    }),
  get: (token: string, convId: string) =>
    request<ConversationOut>(`/conversations/${convId}`, { token }),
  archive: (token: string, convId: string) =>
    request<void>(`/conversations/${convId}`, { method: "DELETE", token }),
  rename: (token: string, convId: string, title: string) =>
    request<ConversationOut>(`/conversations/${convId}`, {
      method: "PATCH",
      json: { title },
      token,
    }),
  previewFile: (token: string, convId: string, aref: string, metadataOnly = false) =>
    request<FilePreviewResponse>(
      `/conversations/${convId}/files/${aref}/preview${metadataOnly ? "?metadata_only=true" : ""}`,
      { token },
    ),
  messages: (token: string, convId: string) =>
    request<MessageListResponse>(`/conversations/${convId}/messages`, { token }),
  uploadFile: (token: string, convId: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return request<AttachmentOut>(`/conversations/${convId}/files`, {
      form: fd,
      token,
    });
  },
  deleteFile: (token: string, convId: string, aref: string) =>
    request<void>(`/conversations/${convId}/files/${aref}`, {
      method: "DELETE",
      token,
    }),
  fileUrl: (convId: string, aref: string) => `/conversations/${convId}/files/${aref}`,
};

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const asciiMatch = /filename="?([^";]+)"?/i.exec(header);
  return asciiMatch?.[1] ?? null;
}

export async function downloadConversationFile(
  token: string,
  convId: string,
  aref: string,
  suggestedName: string,
): Promise<void> {
  const res = await fetch(conversationsApi.fileUrl(convId, aref), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw await parseError(res);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const responseName = filenameFromContentDisposition(res.headers.get("content-disposition"));
  a.href = url;
  a.download = responseName || suggestedName;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Chat-turn SSE events emitted by POST /conversations/{id}/messages
export type ChatEvent =
  | { type: "turn_start"; data: Record<string, never> }
  | { type: "reasoning_token"; data: { delta: string } }
  | { type: "assistant_token"; data: { delta: string } }
  | {
      type: "tool_call_start";
      data: { tool: string; call_id: string; args: Record<string, unknown> };
    }
  | {
      type: "tool_call_end";
      data: {
        tool: string;
        call_id: string;
        outcome: "success" | "error";
        error_code: string | null;
        duration_ms: number;
        preview: string;
        preview_truncated?: boolean;
      };
    }
  | { type: "message_done"; data: { message_id: string; stop_reason: string } }
  | { type: "memory_learned"; data: { count: number } }
  | { type: "error"; data: { code: string; message: string } };

/**
 * POST a user message and stream the SSE response by parsing the raw body.
 * Callback fires once per SSE frame; promise resolves on done|error|stream end.
 */
export interface ChatTurnRequestOptions {
  kind?: "user_message" | "workflow_save_request";
  workflowName?: string;
  workflowDescription?: string;
  cellRef?: { cell: string; text: string; fresh?: string };
}

export async function streamChatTurn(
  token: string,
  convId: string,
  content: string,
  onEvent: (e: ChatEvent) => void,
  signal?: AbortSignal,
  options: ChatTurnRequestOptions = {},
): Promise<void> {
  const res = await fetch(`/conversations/${convId}/messages`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      kind: options.kind ?? "user_message",
      content,
      cell_ref: options.cellRef ?? null,
      workflow_name: options.workflowName ?? null,
      workflow_description: options.workflowDescription ?? null,
    }),
    signal,
  });
  if (!res.ok || !res.body) {
    throw await parseError(res);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // Process complete SSE frames separated by \n\n
    let idx: number;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const ev = parseSseFrame(frame);
      if (ev) onEvent(ev);
    }
  }
}

// ---- workflows (saved task templates) -----------------------------------

export interface WorkflowInputSlot {
  slot: string;
  original_name: string;
  schema_hint: {
    sheets: string[];
    key_columns: string[];
  };
}

export interface WorkflowStep {
  op: string; // inspect_file | run_rule | render_report | render_spreadsheet
  // remaining fields vary by op — keep loose for the detail page
  [k: string]: unknown;
}

export interface WorkflowContractIssue {
  severity: "block" | "warn" | string;
  code: string;
  message: string;
  step?: number;
  op?: string;
}

export interface WorkflowDag {
  nodes: Array<{
    id: string;
    index: number;
    op: string;
    inputs?: string[];
    outputs?: string[];
  }>;
  edges: Array<{
    from: string;
    to: string;
    ref?: string;
  }>;
}

export interface WorkflowAudit {
  run_python_step_count?: number;
  run_python_steps?: Array<Record<string, unknown>>;
}

export interface WorkflowSpec {
  version: number;
  contract_version?: number;
  spec_migration?: {
    status: string;
    original_version?: number | null;
    current_version?: number;
    applied?: Array<Record<string, unknown>>;
    message?: string;
  };
  summary: string;
  input_slots: WorkflowInputSlot[];
  steps: WorkflowStep[];
  dry_run?: {
    status: "ok" | "blocked" | string;
    checked_steps?: number;
    issues?: WorkflowContractIssue[];
  };
  dag?: WorkflowDag;
  audit?: WorkflowAudit;
}

export interface WorkflowOut {
  workflow_id: string;
  user_id: string;
  name: string;
  description: string | null;
  source_conv_id: string;
  spec: WorkflowSpec;
  created_at: number;
  updated_at: number;
  last_run_at: number | null;
  run_count: number;
  archived: boolean;
}

export interface WorkflowListResponse {
  workflows: WorkflowOut[];
}

export interface WorkflowRunOut {
  workflow_run_id: string;
  workflow_id: string;
  user_id: string;
  conv_id: string;
  status: "success" | "error" | string;
  started_at: number;
  finished_at: number | null;
  duration_ms: number | null;
  error_code: string | null;
  error_message: string | null;
  trace: Array<Record<string, unknown>>;
  final_output: Record<string, unknown> | null;
  contract: Record<string, unknown> | null;
}

export interface WorkflowRunListResponse {
  runs: WorkflowRunOut[];
}

export const workflowsApi = {
  list: (token: string, archived = false) =>
    request<WorkflowListResponse>(`/workflows?archived=${archived ? "true" : "false"}`, { token }),
  get: (token: string, workflowId: string) =>
    request<WorkflowOut>(`/workflows/${workflowId}`, { token }),
  create: (
    token: string,
    body: {
      conv_id: string;
      name: string;
      description?: string | null;
      selected_tool_call_ids?: string[] | null;
      final_tool_call_id?: string | null;
      curation_note?: string | null;
    },
  ) => request<WorkflowOut>("/workflows", { method: "POST", json: body, token }),
  patch: (
    token: string,
    workflowId: string,
    body: { name?: string; description?: string | null },
  ) =>
    request<WorkflowOut>(`/workflows/${workflowId}`, {
      method: "PATCH",
      json: body,
      token,
    }),
  delete: (token: string, workflowId: string) =>
    request<void>(`/workflows/${workflowId}`, { method: "DELETE", token }),
  listRuns: (token: string, workflowId: string) =>
    request<WorkflowRunListResponse>(`/workflows/${workflowId}/runs`, { token }),
  getRun: (token: string, workflowId: string, runId: string) =>
    request<WorkflowRunOut>(`/workflows/${workflowId}/runs/${runId}`, { token }),
  bumpRun: (token: string, workflowId: string) =>
    request<WorkflowOut>(`/workflows/${workflowId}/run`, {
      method: "POST",
      token,
    }),
};

// ---- rulesets -----------------------------------------------------------

export interface RulesetOut {
  slug: string;
  title: string;
  content: string;
  version: number;
  updated_at: number;
  updated_by: string | null;
}

export interface ConfirmRulesetEditResponse {
  slug: string;
  version: number;
  change_summary: string;
}

export interface PendingRulesetEditResponse {
  status: "none" | "pending_confirmation";
  pending_id?: string;
  slug?: string;
  base_version?: number;
  change_summary?: string;
  before_snippet?: string;
  after_snippet?: string;
  created_at?: number;
}

export const rulesetApi = {
  get: (token: string, slug: string) => request<RulesetOut>(`/rulesets/${slug}`, { token }),
  save: (
    token: string,
    slug: string,
    body: { content: string; expected_version: number; change_summary?: string },
  ) => request<RulesetOut>(`/rulesets/${slug}`, { method: "PUT", token, json: body }),
  latestPendingEdit: (token: string) =>
    request<PendingRulesetEditResponse>("/rulesets/edits/pending/latest", { token }),
  confirmEdit: (token: string, pendingId: string) =>
    request<ConfirmRulesetEditResponse>(`/rulesets/edits/${pendingId}/confirm`, {
      method: "POST",
      token,
    }),
  cancelEdit: (token: string, pendingId: string) =>
    request<void>(`/rulesets/edits/${pendingId}/cancel`, { method: "POST", token }),
};

// ---- cell-rule calibration ---------------------------------------------

export interface CalibrationArmMeta {
  arm_id: string;
  label: string;
  batch_id: string | null;
  sample_count: number;
  business_key_count: number;
}

export interface CalibrationArmsResponse {
  arms: CalibrationArmMeta[];
  default_arm: string;
}

export interface CalibrationCell {
  business_key: string;
  label: string;
  business_question: string;
  samples: string[];
  case_count: number;
  mapped: boolean;
  runnable: boolean;
  calibration_kind: "rule_generation" | "derived_enum";
  publication_eligible: boolean;
  online_report_ref: string | null;
  snapshot_statuses: Array<{ sample_id: string; status: string }>;
  input_preflight: {
    contract_version: number;
    status: "ready" | "blocked";
    runnable: boolean;
    blocker_count: number;
    cases: Array<{
      sample_id: string;
      status: string;
      ready: boolean;
      note: string;
      blockers: Array<{ code: string; message: string }>;
      required_labels: string[];
      evidence_labels: string[];
      source_refs: string[];
      sources: Array<{
        file: string;
        sheet: string;
        cell: string;
        source_ref: string;
        label: string;
      }>;
    }>;
  };
  state: string;
  latest: {
    iteration: number;
    status: string;
    pass_mode: string | null;
    rule_version: number;
    review_blockers: string[];
  };
}

export interface CalibrationPreflightResponse {
  arm_id: string;
  arm_label: string;
  batch_id: string;
  protocol_version: number;
  sample_count: number;
  business_key_count: number;
  runnable_count: number;
  unmapped_count: number;
  cells: CalibrationCell[];
  execution_enabled: boolean;
  configuration_ready: boolean;
  configuration_errors: string[];
  active_job: CalibrationJob | null;
  latest_job: CalibrationJob | null;
}

export interface CalibrationJob {
  run_id: string;
  mode: "all" | "pilot" | "resume" | "single";
  business_key: string;
  reopen_passed: boolean;
  state: "queued" | "running" | "done" | "failed" | "cancelled" | "interrupted";
  current_key: string;
  current_iteration: number;
  target_keys: string[];
  active_keys: string[];
  finished_count: number;
  total_count: number;
  worker_statuses: Record<string, string>;
  max_workers: number;
  started_at: number | null;
  updated_at: number;
  finished_at: number | null;
  return_code: number | null;
  recent_logs: string[];
}

export interface CalibrationReview {
  pass?: boolean;
  scores?: Record<string, number>;
  critical_issues?: unknown[];
  not_aligned?: unknown[];
  missing_points?: unknown[];
  unsupported_claims?: unknown[];
  repeated_content?: unknown[];
  summary?: string;
}

export interface CalibrationEvidenceItem {
  source_ref?: string;
  label?: string;
  evidence_role?: string;
  file?: string;
  sheet?: string;
  cell?: string;
  selector?: string;
  value?: string;
  verification?: string;
}

export interface CalibrationRequestAudit {
  audit_version: number;
  sequence_step: number;
  stage: "generate_candidate" | "judge_candidate";
  source: "recorded_at_execution" | "reconstructed_from_record";
  model: string;
  system_prompt: string;
  user_prompt: string;
  request_sha256: string;
  recorded_request_sha256?: string;
  hash_verified?: boolean;
  human_gold_included: boolean;
  output_semantic_selector?: string;
  evidence_items?: CalibrationEvidenceItem[];
  leak_check?: {
    status: "passed" | "blocked";
    findings: string[];
  };
}

export interface CalibrationCaseRecord {
  sample_id: string;
  human_gold?: string;
  normalized_human_gold?: string;
  output_semantic_selector?: string;
  calibration_kind?: "rule_generation" | "derived_enum";
  publication_eligible?: boolean;
  raw_candidate?: string;
  candidate_normalization?: {
    kind: string;
    allowed_outputs: string[];
    normalized: string;
    changed: boolean;
  };
  candidate?: string;
  pass_mode?: string;
  generator_error?: string;
  judge_error?: string;
  judge_attempts?: number;
  judge_retry_errors?: Array<{
    attempt: number;
    error: string;
    retryable: boolean;
    delay_seconds?: number;
  }>;
  generator_elapsed?: number;
  judge_elapsed?: number;
  generator_usage?: Record<string, unknown>;
  judge_usage?: Record<string, unknown>;
  generator_request_sha256?: string;
  judge_request_sha256?: string;
  generation_audit?: CalibrationRequestAudit;
  judgement_audit?: CalibrationRequestAudit;
  gpt_review?: CalibrationReview;
}

export interface CalibrationRevisionArtifact {
  handoff_path: string | null;
  revision_path: string | null;
  revision_failure_path: string | null;
  revision_signals: Array<Record<string, unknown>>;
  case_revision_profiles: Array<Record<string, unknown>>;
  style_profile: string;
  revised_rule: Record<string, unknown> | null;
  case_synthesis: Record<string, unknown> | null;
  change_log: Array<Record<string, unknown>>;
  unresolved_issues: unknown[];
  reviser_model: string;
  revision_attempts: Array<{
    attempt: number;
    status: "accepted" | "rejected";
    error: string | null;
  }>;
  revision_error: string | null;
  revision_status: "completed" | "failed" | "requested" | "not_needed";
}

export interface CalibrationFailureIssue {
  issue_type: string;
  issue_type_label: string;
  dimension?: string;
  severity?: string;
  human_excerpt?: string;
  candidate_excerpt?: string;
  difference?: string;
  revision_instruction?: string;
}

export interface CalibrationFailureReason {
  sample_id: string;
  project_name?: string;
  kind: "generation_error" | "judgement_error" | "not_aligned";
  reason: string;
  pass_mode?: string;
  generator_error?: string;
  judge_error?: string;
  issues?: CalibrationFailureIssue[];
}

export interface CalibrationRoundRecord {
  iteration: number;
  created_at?: string;
  cell_status: string;
  pass_mode: string;
  rule_version: number;
  rule_sha256?: string;
  rule?: Record<string, unknown>;
  generator_model?: string;
  judge_model?: string;
  review_blockers?: string[];
  failure_reasons?: CalibrationFailureReason[];
  manual_reopen?: {
    reason: "manual_alignment_rejection";
    previous_iteration: number;
    previous_rule_version: number;
    reopened_at: string;
  };
  cases: CalibrationCaseRecord[];
  next_action: string;
  artifacts: CalibrationRevisionArtifact;
}

export interface CalibrationCellHistoryResponse {
  cell: CalibrationCell;
  rounds: CalibrationRoundRecord[];
  round_count: number;
  final_status: string;
  completed: boolean;
  latest_iteration: number;
  latest_next_action: string;
}

export interface CalibrationRunLogsResponse {
  run_id: string;
  source: "complete_file" | "snapshot_fallback";
  truncated: boolean;
  offset: number;
  next_offset: number;
  total_lines: number;
  has_more: boolean;
  lines: string[];
}

export interface CalibrationCellDetailResponse {
  cell: CalibrationCell;
  latest_record: {
    iteration: number;
    cell_status: string;
    pass_mode: string;
    rule_version: number;
    cases: CalibrationCaseRecord[];
  } | null;
}

export const calibrationApi = {
  arms: (token: string) =>
    request<CalibrationArmsResponse>("/calibration/arms", { token }),
  preflight: (token: string, arm = "online") =>
    request<CalibrationPreflightResponse>(`/calibration/preflight?arm=${encodeURIComponent(arm)}`, {
      token,
    }),
  start: (
    token: string,
    body: {
      mode: "all" | "pilot" | "resume" | "single";
      business_key?: string;
      reopen_passed?: boolean;
    },
    arm = "online",
  ) =>
    request<CalibrationJob>(
      `/calibration/runs?arm=${encodeURIComponent(arm)}`,
      { method: "POST", json: body, token },
    ),
  status: (token: string, runId: string) =>
    request<CalibrationJob>(`/calibration/runs/${runId}`, { token }),
  cell: (token: string, businessKey: string, arm = "online") =>
    request<CalibrationCellDetailResponse>(
      `/calibration/cells/${encodeURIComponent(businessKey)}?arm=${encodeURIComponent(arm)}`,
      { token },
    ),
  history: (token: string, businessKey: string, arm = "online") =>
    request<CalibrationCellHistoryResponse>(
      `/calibration/cells/${encodeURIComponent(businessKey)}/history?arm=${encodeURIComponent(arm)}`,
      { token },
    ),
  logs: (token: string, runId: string, offset = 0, limit = 2000) =>
    request<CalibrationRunLogsResponse>(
      `/calibration/runs/${encodeURIComponent(runId)}/logs?offset=${offset}&limit=${limit}`,
      { token },
    ),
  cancel: (token: string, runId: string) =>
    request<CalibrationJob>(`/calibration/runs/${runId}/cancel`, { method: "POST", token }),
};

// ---- data sources -------------------------------------------------------

export interface UserDataSourceCapability {
  is_dynamic: boolean;
  tags: string[];
  extraction_hints: Record<string, unknown>;
}

export interface UserDataSource {
  source_id: string;
  user_id: string;
  name: string;
  description: string;
  url_pattern: string;
  capability: UserDataSourceCapability;
  enabled: boolean;
  created_at: number;
  updated_at: number;
}

export interface DataSourceListResponse {
  sources: UserDataSource[];
}

export interface CreateDataSourceRequest {
  // name is auto-generated server-side when omitted (frontend never asks
  // for it). capability defaults to dynamic SPA when omitted.
  name?: string;
  description: string;
  url_pattern: string;
  capability?: UserDataSourceCapability;
}

export interface PatchDataSourceRequest {
  description?: string;
  url_pattern?: string;
  capability?: UserDataSourceCapability;
  enabled?: boolean;
}

export const dataSourcesApi = {
  list: (token: string, enabledOnly = false) => {
    const q = enabledOnly ? "?enabled=true" : "";
    return request<DataSourceListResponse>(`/data-sources${q}`, { token });
  },
  get: (token: string, sourceId: string) =>
    request<UserDataSource>(`/data-sources/${sourceId}`, { token }),
  create: (token: string, body: CreateDataSourceRequest) =>
    request<UserDataSource>(`/data-sources`, {
      method: "POST",
      json: body as unknown as Record<string, unknown>,
      token,
    }),
  patch: (token: string, sourceId: string, body: PatchDataSourceRequest) =>
    request<UserDataSource>(`/data-sources/${sourceId}`, {
      method: "PATCH",
      json: body as unknown as Record<string, unknown>,
      token,
    }),
  delete: (token: string, sourceId: string) =>
    request<void>(`/data-sources/${sourceId}`, { method: "DELETE", token }),
};

function parseSseFrame(frame: string): ChatEvent | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event: ")) event = line.slice(7).trim();
    else if (line.startsWith("data: ")) dataLines.push(line.slice(6));
    else if (line.startsWith(":")) {
      // comment / heartbeat
    }
  }
  if (!dataLines.length) return null;
  const raw = dataLines.join("\n");
  let data: unknown = {};
  try {
    data = JSON.parse(raw);
  } catch {
    data = { raw };
  }
  return { type: event, data } as ChatEvent;
}

// ---- land data view (地块数据页: 搜索数据 + 证据) ----------------------------

export interface LandDataSource {
  label: string;
  url: string;
  domain: string;
  screenshot?: string;
  year?: string | number;
  period?: string;
  note?: string;
  publisher?: string;
  grade?: string;
  confidence?: string;
  snippet?: string;
}

/** 证据状态分级(只描述证据, 不评价数值好坏), 见 data_view.LEVELS */
export type LandDataLevelKey = "verified" | "single" | "review" | "fallback" | "unpublished" | "pending";

export interface LandDataLevel {
  level: LandDataLevelKey;
  note: string;
}

export interface LandDataGap {
  domain: "location" | "roads" | "enterprise" | "districts" | "zones" | "land" | "other";
  kind: "pending" | "unpublished" | "error";
  title: string;
  detail: string;
}

export interface LandDataMetricStatus {
  gdp: LandDataLevel;
  industrial_output: LandDataLevel;
  industrial_firms: LandDataLevel;
}

export interface LandDataLocationDim {
  level?: LandDataLevel;
  field: string;
  value: string;
  basis: string;
  source: string;
  source_type: string;
  grade: string;
  confidence: string;
  url: string;
  domain: string;
  screenshot: string;
}

export interface LandDataMap {
  maptype: string;
  label: string;
  asset: string;
}

export interface LandDataRoadReference {
  title: string;
  url: string;
  domain: string;
  official: boolean;
  provider: string;
  fact: string;
  temporal: string;
}

export interface LandDataRoad {
  road: string;
  side: string;
  distance_m: number | null;
  status: string;
  status_reason: string;
  fact: string;
  fact_source: string;
  tile: string;
  tile_center: string;
  satellite_status: string;
  references: LandDataRoadReference[];
  level?: LandDataLevel;
}

export interface LandDataEnterprise {
  provider: string;
  range_type: string;
  active_total: number | null;
  total: number | null;
  scope: string;
  all_enterprises: number | null;
  all_enterprises_capped: boolean;
  individual_biz_count: number | null;
  non_individual_count: number | null;
  radius_km: number | null;
  center: { lat: number | null; lng: number | null; coord_system: string };
  source: string;
  source_url: string;
  industries: Array<{
    name: string;
    count: number;
    children: Array<{
      name: string;
      code: string;
      count: number;
      count_with_individual: number | null;
      individual_count: number | null;
    }>;
  }>;
  failures: string[];
  level?: LandDataLevel;
}

export interface LandDataDistrict {
  is_target?: boolean;
  status?: LandDataMetricStatus;
  name: string;
  gdp: number | null;
  gdp_year: number | string;
  /** 无绝对值时采集库的结论(官方仅发布增速/未公布), 有值则替代"未取到"提示 */
  gdp_note?: string;
  industrial_output: number | null;
  industrial_output_kind: string;
  industrial_year: number | string;
  industrial_firms: number | null;
  industrial_firms_year?: number | string;
  sources: {
    gdp: LandDataSource | null;
    industrial_output: LandDataSource | null;
    industrial_firms: LandDataSource | null;
  };
  note: string;
}

export interface LandDataZone {
  status?: LandDataMetricStatus;
  name: string;
  gdp: number | null;
  gdp_year: number | string;
  industrial_output: number | null;
  industrial_output_kind: string;
  industrial_year: number | string;
  industrial_firms: number | null;
  industrial_firms_year?: number | string;
  sources: {
    gdp: LandDataSource | null;
    industrial_output: LandDataSource | null;
    industrial_firms: LandDataSource | null;
  };
}

export interface LandDataLandSupplyRow {
  is_target?: boolean;
  name: string;
  parcels: number | null;
  under_30mu: number | null;
  share_under_30mu: number | null;
  under_15mu: number | null;
  share_under_15mu: number | null;
}

export interface LandDataLandSupply extends LandDataLandSupplyRow {
  region: string;
  region_level: string;
  window: string;
  source: string;
  source_url: string;
  note: string;
  districts: LandDataLandSupplyRow[];
  district_errors: Record<string, string>;
  level?: LandDataLevel;
}

export type LandRegionTier = "推荐" | "可考虑" | "回避";

export interface LandRegionSector {
  sector: string;
  latest: number | null;
  latest_year: number | null;
  growth_pct: number | null;
  heavy: boolean;
}

/** 区域研判对比表的一行(数字由后端确定性计算, 模型不改这些数)。 */
export interface LandRegionRow {
  district: string;
  is_target: boolean;
  tier: LandRegionTier | "";
  gdp: number | null;
  gdp_year: number | null;
  rank_gdp: number | null;
  gdp_cagr_pct: number | null;
  gdp_span: string;
  rank_gdp_cagr_pct: number | null;
  area_km2: number | null;
  density: number | null;
  rank_density: number | null;
  output: number | null;
  output_kind: string;
  output_year: number | null;
  rank_output: number | null;
  firms: number | null;
  rank_firms: number | null;
  new_reg: number | null;
  new_reg_year: number | null;
  new_reg_growth_pct: number | null;
  new_reg_span: string;
  new_reg_caliber: string;
  new_reg_is_flow: boolean;
  rank_new_reg: number | null;
  elec: number | null;
  elec_year: number | null;
  elec_unit: string;
  elec_growth_pct: number | null;
  elec_span: string;
  elec_is_industrial: boolean;
  elec_caliber: string;
  land_parcels_5y: number | null;
  land_area_mu_5y: number | null;
  rank_land_area_mu_5y: number | null;
  land_price_latest: number | null;
  land_price_year: string | null;
  land_price_span: string;
  land_price_trend_pct: number | null;
  land_small_share_first: number | null;
  land_small_share_last: number | null;
  sectors_growth_only: boolean;
  top_sectors: LandRegionSector[];
  /** 规上营收远超 GDP(>4 倍), 疑似统计范围不同, 不排名不参与判断 */
  output_suspect?: boolean;
  new_reg_class?: string;
  new_reg_cagr_pct?: number | null;
  elec_cagr_pct?: number | null;
  rank_land_price_latest?: number | null;
  /** 规则初判: 产业需求 / 拿地条件 = 偏强 | 中等 | 偏弱 | 数据不足 */
  demand_label?: string;
  land_label?: string;
  demand_basis?: string;
  land_basis?: string;
  /** 各排名的可比区数(同口径同年份才排名) */
  rank_gdp_n?: number | null;
  rank_gdp_cagr_pct_n?: number | null;
  rank_density_n?: number | null;
  rank_output_n?: number | null;
  rank_firms_n?: number | null;
  rank_new_reg_n?: number | null;
  rank_land_area_mu_5y_n?: number | null;
  rank_land_price_latest_n?: number | null;
  /** 常住人口(万人)与近三年变化 */
  population?: number | null;
  population_year?: number | null;
  pop_growth_pct?: number | null;
  /** 近三年年均增速(各区最新年份可能不同, 排名用年均) */
  pop_cagr_pct?: number | null;
  pop_span?: string;
  rank_population?: number | null;
  rank_population_n?: number | null;
  rank_pop_cagr_pct?: number | null;
  rank_pop_cagr_pct_n?: number | null;
  /** 人均 GDP(万元) */
  gdp_per_capita?: number | null;
  rank_gdp_per_capita?: number | null;
  rank_gdp_per_capita_n?: number | null;
  /** 区内有校区的高校数(其中本科 / 高职) */
  universities?: number | null;
  universities_undergrad?: number | null;
  universities_vocational?: number | null;
  universities_year?: number | null;
  universities_caliber?: string;
  universities_names?: string;
}

export interface LandRegionAnalysis {
  headline: string;
  city_backdrop: string;
  tiers: Array<{ district: string; tier: LandRegionTier; reason: string }>;
  target: { district: string; tier: LandRegionTier; verdict: string } | null;
  districts: Array<{ district: string; demand: string; land: string; risks: string }>;
  aspects: Partial<Record<"economy" | "density" | "activity" | "talent" | "industry" | "electricity" | "land", string>>;
  gaps: string[];
}

export interface LandRegion {
  status: "ok" | "error" | "skipped";
  error: string;
  city: string;
  target_district: string;
  n_districts: number | null;
  analysis: LandRegionAnalysis | null;
  table: LandRegionRow[];
  land: { source?: string; window?: string; truncated?: boolean; incomplete_years?: string[]; years?: string[] };
  model: string;
  generated_at: string;
}

/** 一条数的出处(区域研判数据快照, 点开看来源与原文) */
export interface LandRegionSrc {
  year: number | null;
  sub: string;
  value: number | null;
  unit: string;
  caliber: string;
  title: string;
  url: string;
  quote: string;
}

/** 城市级指标的逐年序列 */
export interface LandRegionCitySeries {
  series: Record<string, number>;
  latest: number | null;
  latest_year: number | null;
  growth_pct: number | null;
  span: string;
  unit: string;
  caliber: string;
}

export interface LandRegionSectorFull extends LandRegionSector {
  span?: string;
  rank?: number;
  rank_ex_heavy?: number;
  /** 逐年产值(仅增速的区为逐年增速) */
  series?: Record<string, number>;
}

/** 规上工业逐年: yearbook = 省统计年鉴县区表(各区同口径), own = 本区公报 / 采集库(年鉴未出的年份) */
export interface LandRegionIndustryYear {
  year: number;
  output: number | null;
  firms: number | null;
  /** 同比只在同一来源内计算 */
  output_yoy_pct: number | null;
  source: "yearbook" | "own";
}

/** 国信网逐宗成交按年汇总 */
export interface LandRegionLandYear {
  parcels: number;
  area_mu: number;
  total_price_yi?: number;
  avg_price_wan_per_mu: number | null;
  small_share: number | null;
}

/** 实际增速走势: 近三年均值 vs 此前三年均值 */
export interface LandRegionGrowthTrend {
  recent_avg?: number;
  recent_span?: string;
  early_avg?: number;
  early_span?: string;
  change_pp?: number;
}

/** 第五次经济普查(2023, 全部工业企业)按研判口径剥离重化工后的结构 */
export interface LandRegionCensus {
  year: number;
  total_revenue: number;
  total_enterprises: number | null;
  listed_industries: number;
  partial: boolean;
  heavy: Array<{ sector: string; revenue: number }>;
  heavy_revenue: number;
  heavy_share_pct: number;
  revenue_after_strip: number;
  enterprises_after_strip: number | null;
  top_by_revenue: Array<{ sector: string; revenue: number; share_pct: number }>;
  top_by_enterprises: Array<{ sector: string; enterprises: number }>;
  source_title: string;
  source_url: string;
}

export type LandRegionSizeKey = "large" | "medium" | "small" | "micro" | "small_micro" | "sme" | "total";

/** 中小企业(中型 + 小微型)逐年合计与首末年变化 */
export interface LandRegionSme {
  first_year: string;
  last_year: string;
  total: Record<string, number>;
  change_pct: number | null;
}

export interface LandRegionDataDistrict {
  district: string;
  is_target: boolean;
  economy: {
    gdp: number | null;
    gdp_year: number | null;
    gdp_series: Record<string, number>;
    gdp_cagr_pct: number | null;
    gdp_span: string;
    area_km2: number | null;
    density: number | null;
    /** 官方实际增速(可比价)逐年, % */
    gdp_real_growth?: Record<string, number>;
    gdp_real_recent_pct?: number | null;
    gdp_real_recent_span?: string;
    gdp_real_early_pct?: number | null;
    gdp_real_early_span?: string;
    gdp_real_trend_pp?: number | null;
    secondary_growth?: Record<string, number>;
  };
  industry: {
    output: number | null;
    output_kind: string;
    output_year: number | null;
    output_growth_pct: number | null;
    output_span: string;
    output_suspect?: boolean;
    firms: number | null;
    firms_year: number | null;
    industry_years?: LandRegionIndustryYear[];
    /** 规上工业企业按规模分(区统计年鉴) */
    size_structure?: Partial<Record<LandRegionSizeKey, LandRegionCitySeries>>;
    sme?: LandRegionSme | null;
  };
  new_reg: {
    latest: number | null;
    year: number | null;
    series: Record<string, number>;
    cagr_pct: number | null;
    span: string;
    caliber: string;
    is_flow: boolean | null;
    class: string;
  };
  people: {
    population: number | null;
    population_year: number | null;
    pop_series?: Record<string, number>;
    pop_cagr_pct: number | null;
    pop_span: string;
    gdp_per_capita: number | null;
    universities: number | null;
    universities_undergrad: number | null;
    universities_vocational: number | null;
    universities_names: string;
    universities_caliber: string;
  };
  electricity: {
    latest: number | null;
    year: number | null;
    unit: string;
    series: Record<string, number>;
    series_all?: Record<string, number>;
    cagr_pct: number | null;
    span: string;
    caliber: string;
    is_industrial: boolean | null;
  };
  sectors: {
    growth_only: boolean;
    items: LandRegionSectorFull[];
    firms?: LandRegionSectorFull[];
    /** 产值年鉴只到较早年份时, 公报更近年份的分行业增速 */
    growth_recent?: LandRegionSectorFull[];
  };
  land?: {
    by_year: Record<string, LandRegionLandYear>;
    parcels_5y: number | null;
    area_mu_5y: number | null;
    price_latest: number | null;
    price_year: string | null;
  };
  sources: Partial<
    Record<
      | "gdp"
      | "gdp_growth"
      | "secondary_growth"
      | "industry"
      | "area"
      | "new_reg"
      | "population"
      | "universities"
      | "electricity"
      | "sectors"
      | "sector_firms"
      | "sector_growth"
      | "size",
      LandRegionSrc[]
    >
  >;
}

/** 城市概况 + 区县分主题指标(与区域研判同一套数, 搜索时快照) */
export interface LandRegionData {
  city: string;
  target_district: string;
  land_meta?: { source?: string; window?: string; truncated?: boolean; incomplete_years?: string[]; years?: string[] };
  city_profile: {
    gdp?: {
      series: Record<string, number>;
      growth_pct: number | null;
      span: string;
      real_growth?: Record<string, number>;
      real_trend?: LandRegionGrowthTrend;
    };
    industry_growth?: LandRegionCitySeries | null;
    land_by_year?: Record<string, LandRegionLandYear>;
    gdp_national_rank?: LandRegionCitySeries | null;
    population?: LandRegionCitySeries | null;
    universities?: LandRegionCitySeries | null;
    universities_national_rank?: LandRegionCitySeries | null;
    industrial_output?: LandRegionCitySeries | null;
    above_scale_firms?: LandRegionCitySeries | null;
    size_structure?: Partial<Record<LandRegionSizeKey, LandRegionCitySeries>>;
    sme?: LandRegionSme | null;
    /** 行政区域面积(平方公里)与经济密度(最新年 GDP ÷ 面积, 亿元/平方公里) */
    area_km2?: number | null;
    density?: number | null;
    density_year?: number | null;
    new_registrations?: LandRegionCitySeries | null;
    sectors?: LandRegionSectorFull[];
    sectors_growth_only?: boolean;
    census?: LandRegionCensus | null;
    sources?: Record<string, LandRegionSrc[]>;
  };
  districts: LandRegionDataDistrict[];
}

export interface LandDataView {
  conv_id: string;
  project: {
    name: string;
    city: string;
    district: string;
    lat: number | null;
    lng: number | null;
    generated_at: number | null;
  };
  summary: {
    location_dims: number;
    roads: number;
    roads_verified: number;
    enterprise_total: number | null;
    districts: number;
    land_parcels: number | null;
    gaps: number;
    completeness?: Record<string, Partial<Record<LandDataLevelKey, number>>>;
  };
  gaps?: LandDataGap[];
  location: { dims: LandDataLocationDim[]; maps: LandDataMap[]; gaps: string[] };
  roads: { roads: LandDataRoad[]; value: string; source: string; summary: Record<string, unknown> };
  enterprise: LandDataEnterprise | null;
  districts: LandDataDistrict[];
  zones: LandDataZone[];
  land_supply: LandDataLandSupply | null;
  region?: LandRegion | null;
  region_data?: LandRegionData | null;
  errors: string[];
  download_name: string;
}

export const landDataApi = {
  view: (token: string, convId: string, signal?: AbortSignal) =>
    request<LandDataView>(`/land-research/${convId}/data`, { token, signal }),
  assetUrl: (convId: string, asset: string): string =>
    `/land-research/${convId}/data/assets/${asset.split("/").map(encodeURIComponent).join("/")}`,
};

const assetBlobCache = new Map<string, Promise<string>>();

/** Fetch a protected image once and return an object URL (cached per session). */
export function loadLandDataAsset(token: string, convId: string, asset: string): Promise<string> {
  const url = landDataApi.assetUrl(convId, asset);
  const cached = assetBlobCache.get(url);
  if (cached) return cached;
  const pending = fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(async (res) => {
      if (!res.ok) throw await parseError(res);
      return URL.createObjectURL(await res.blob());
    })
    .catch((err) => {
      assetBlobCache.delete(url);
      throw err;
    });
  assetBlobCache.set(url, pending);
  return pending;
}
